import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {Clock, Lock, LockOpen, type LucideIcon} from 'lucide-react';
import {cn} from '@chekinapp/ui';
import {formatDisplayDate} from '../guestForm/helpers';
import {useRemoteAccessEnabled} from '../remoteAccess/hooks';
import {useKeysState, type KeysState} from '../remoteAccess/useKeysState';
import {formatClockLabel} from './dates';
import {useHomeEnv, useHomeNavigate} from './env';
import {HeroBlooms} from './HeroBlooms';
import {useHomeData} from './HomeDataContext';

type DateBlockProps = {
  label: string;
  date?: string;
  time?: string;
  align?: 'left' | 'right';
};

function DateBlock({label, date, time, align = 'left'}: DateBlockProps) {
  const {i18n} = useTranslation();

  const day = formatDisplayDate(date, i18n.language, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

  return (
    <div className={align === 'right' ? 'text-right' : 'text-left'}>
      <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[var(--chekin-blue-200)]">
        {label}
      </p>
      <p className="mt-1 whitespace-nowrap text-[18px] font-semibold leading-tight tracking-[-0.01em] text-white [@container(min-width:520px)]:text-[26px]">
        {day ?? '—'}
      </p>
      {time && (
        <p className="mt-0.5 text-[13px] text-white/80 [@container(min-width:520px)]:text-[15px]">
          {time}
        </p>
      )}
    </div>
  );
}

const TILE_TONE: Record<KeysState, {chip: string; bar: string}> = {
  gated: {
    chip: 'bg-[var(--chekin-amber-400)] text-[var(--chekin-color-brand-navy)]',
    bar: 'bg-[var(--chekin-amber-400)]',
  },
  waiting: {
    chip: 'bg-white/[0.16] text-white',
    bar: 'bg-white/80',
  },
  live: {
    chip: 'bg-[var(--chekin-green-500)] text-[var(--chekin-color-brand-navy)]',
    bar: 'bg-[var(--chekin-green-500)]',
  },
};

const TILE_ICON: Record<KeysState, LucideIcon> = {
  gated: Lock,
  waiting: Clock,
  live: LockOpen,
};

function KeysTile({onClick}: {onClick: () => void}) {
  const {t, i18n} = useTranslation();
  const {state, opensAt} = useKeysState();
  const {navItems} = useHomeData();

  const stepsLeft = navItems.filter(step => !step.completed).length;
  const progress =
    state === 'gated'
      ? navItems.length > 0
        ? (navItems.length - stepsLeft) / navItems.length
        : 0
      : 1;
  const opensTime = opensAt
    ? formatClockLabel(dayjs(opensAt).format('HH:mm'), i18n.language)
    : undefined;
  const opensDay = opensAt
    ? formatDisplayDate(opensAt, i18n.language, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      })
    : undefined;

  const copy = {
    gated: {
      title: t('keys_tile_unlock_title'),
      description: t('keys_tile_locked_desc'),
      chip: t('keys_tile_steps_left', {count: stepsLeft}),
    },
    waiting: {
      title: t('keys_tile_unlock_title'),
      description: opensTime
        ? t('keys_tile_wait_desc', {time: opensTime})
        : t('keys_tile_wait_desc_no_time'),
      chip: opensDay ? t('keys_tile_wait_chip', {date: opensDay}) : undefined,
    },
    live: {
      title: t('your_keys_are_ready'),
      description: t('keys_tile_ready_desc'),
      chip: t('keys_tile_ready_chip'),
    },
  }[state];
  const Icon = TILE_ICON[state];
  const tone = TILE_TONE[state];

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="hero-keys-tile"
      data-state={state}
      className="w-full rounded-[14px] bg-white/[0.1] px-3.5 pb-3 pt-3 text-left text-white transition-colors hover:bg-white/[0.16] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
    >
      <span className="flex items-start gap-2.5">
        <Icon size={18} className="mt-px shrink-0" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block text-[14px] font-semibold leading-5">{copy.title}</span>
          <span className="mt-0.5 block text-[13px] leading-[17px] text-white/75">
            {copy.description}
          </span>
        </span>
        {copy.chip && (
          <span
            className={cn(
              'shrink-0 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[12px] font-bold leading-[18px]',
              tone.chip,
            )}
          >
            {copy.chip}
          </span>
        )}
      </span>
      <span
        aria-hidden
        className="mt-3 block h-1 overflow-hidden rounded-full bg-white/[0.16]"
      >
        <span
          className={cn(
            'block h-full rounded-full transition-[width] duration-500',
            tone.bar,
          )}
          style={{width: `${Math.max(progress, 0.04) * 100}%`}}
        />
      </span>
    </button>
  );
}

export function CheckinHeroStrip() {
  const {t, i18n} = useTranslation();
  const navigate = useNavigate();
  const {navigation} = useHomeEnv();
  const go = useHomeNavigate()(navigate);
  const hasKeys = useRemoteAccessEnabled();
  const {checkInDate, checkInTime, checkOutDate, checkOutTime, nights} = useHomeData();

  const checkIn = formatClockLabel(checkInTime, i18n.language);
  const checkOut = formatClockLabel(checkOutTime, i18n.language);

  return (
    <section
      className="relative overflow-hidden [container-type:inline-size] rounded-[var(--chekin-radius-xl)] px-5 py-5 text-white [background:var(--chekin-gradient-hero)] lg:px-[30px] lg:py-[26px]"
      data-testid="checkin-hero-strip"
      data-entrance="hero"
    >
      <HeroBlooms />

      <div className="relative z-10">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 [@container(min-width:520px)]:gap-6">
          <DateBlock
            label={t('check_in_short')}
            date={checkInDate}
            time={checkIn ? t('from_time', {time: checkIn}) : undefined}
          />
          {typeof nights === 'number' ? (
            <span className="whitespace-nowrap rounded-full bg-white/[0.14] px-2.5 py-1 text-[12px] font-medium text-white [@container(min-width:520px)]:px-3.5 [@container(min-width:520px)]:py-1.5 [@container(min-width:520px)]:text-[13px]">
              {t('nights_count', {count: nights})}
            </span>
          ) : (
            <span />
          )}
          <DateBlock
            label={t('check_out_short')}
            date={checkOutDate}
            time={checkOut ? t('by_time', {time: checkOut}) : undefined}
            align="right"
          />
        </div>

        {hasKeys && (
          <div className="mt-5 border-t border-white/[0.18] pt-4">
            <KeysTile
              onClick={() =>
                go(navigation.remoteAccess, 'hero', {destination: 'remote_access'})
              }
            />
          </div>
        )}
      </div>
    </section>
  );
}
